import { useEffect, useState } from "react";
import { decodeEntities } from "@/lib/sanitize";
import { useSearchParams } from "react-router-dom";
import TiltCard from "@/components/TiltCard";
import AudioPlayer from "@/components/AudioPlayer";
import Header from "@/components/Header";
import SearchBar from "@/components/SearchBar";
import TrackedTopics from "@/components/TrackedTopics";
import PlaylistManager from "@/components/PlaylistManager";
import NotesLibrary from "@/components/NotesLibrary";
import EmptySavedEpisodes from "@/components/empty-states/EmptySavedEpisodes";
import OfflineEpisodeButton from "@/components/OfflineEpisodeButton";
import { Bookmark, Trash2, ExternalLink, Bell, ListMusic, Play, Pause, StickyNote, Settings } from "lucide-react";
import NoteButton from "@/components/NoteButton";
import ShareEpisode from "@/components/ShareEpisode";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SavedEpisode {
  id: string;
  episode_id: string;
  created_at: string;
  episode: {
    id: string;
    title: string;
    slug: string;
    description: string | null;
    audio_url: string | null;
    published_at: string | null;
    duration_seconds: number | null;
    spotify_episode_url: string | null;
    apple_episode_url: string | null;
    podcast: {
      name: string;
      artwork_url: string | null;
      spotify_url: string | null;
    };
  };
}

export default function SavedEpisodes() {
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [saves, setSaves] = useState<SavedEpisode[]>([]);
  const [loading, setLoading] = useState(true);
  const initialTab = (searchParams.get("tab") as "episodes" | "topics" | "playlists" | "notes") || "episodes";
  const [tab, setTab] = useState<"episodes" | "topics" | "playlists" | "notes">(initialTab);
  const [playingId, setPlayingId] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }

    supabase
      .from("saved_episodes")
      .select("id, episode_id, created_at, episode:episodes(id, title, slug, description, audio_url, published_at, duration_seconds, spotify_episode_url, apple_episode_url, podcast:podcasts(name, artwork_url, spotify_url))")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (error) {
          console.error("Failed to load saved episodes:", error);
        } else {
          setSaves((data as unknown as SavedEpisode[]) || []);
        }
        setLoading(false);
      });
  }, [user, authLoading]);

  const handleRemove = async (saveId: string) => {
    await supabase.from("saved_episodes").delete().eq("id", saveId);
    setSaves((prev) => prev.filter((s) => s.id !== saveId));
    toast.success("Episode removed");
  };

  const formatDuration = (s: number | null) => {
    if (!s) return "";
    return `${Math.floor(s / 60)} min`;
  };

  const togglePlay = (episodeId: string) => {
    if (playingId === episodeId) {
      setPlayingId(null);
    } else {
      setPlayingId(episodeId);
    }
  };

  // Not logged in
  if (!authLoading && !user) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header compact />
        <main className="flex-1 container py-12">
          <div className="max-w-2xl mx-auto text-center">
            <Bookmark className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Saved Episodes</h1>
            <p className="font-mono text-sm text-muted-foreground mb-6">
              Sign in to save your favorite podcast episodes and access them anytime.
            </p>
            <Link
              to="/login"
              className="inline-block font-mono font-bold text-sm px-6 py-3 bg-primary text-primary-foreground hover:bg-retro-amber transition-colors"
            >
              Sign In
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header compact />
      <main className="flex-1 container px-4 sm:px-6 py-6 sm:py-10">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-1">
            <h1 className="font-retro font-bold text-xl sm:text-2xl">My Library</h1>
            <Link
              to="/profile"
              title="Account settings"
              aria-label="Account settings"
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors border-2 border-transparent hover:border-border"
            >
              <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
            </Link>
          </div>

          <div className="flex gap-1 mb-6 border-b-2 border-border">
            <button
              onClick={() => setTab("episodes")}
              className={`font-mono font-bold text-xs sm:text-sm px-4 py-2 -mb-[2px] border-b-2 transition-colors ${
                tab === "episodes"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Bookmark className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Episodes ({saves.length})
            </button>
            <button
              onClick={() => setTab("topics")}
              className={`font-mono font-bold text-xs sm:text-sm px-4 py-2 -mb-[2px] border-b-2 transition-colors ${
                tab === "topics"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Bell className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Tracked Topics
            </button>
            <button
              onClick={() => setTab("playlists")}
              className={`font-mono font-bold text-xs sm:text-sm px-4 py-2 -mb-[2px] border-b-2 transition-colors ${
                tab === "playlists"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <ListMusic className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Playlists
            </button>
            <button
              onClick={() => setTab("notes")}
              className={`font-mono font-bold text-xs sm:text-sm px-4 py-2 -mb-[2px] border-b-2 transition-colors ${
                tab === "notes"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <StickyNote className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Notes
            </button>
          </div>

          {tab === "topics" && <TrackedTopics />}
          {tab === "playlists" && <PlaylistManager />}
          {tab === "notes" && <NotesLibrary />}

          {tab === "episodes" && (
            <>


          {loading && (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="retro-border bg-card p-4 animate-pulse h-24" />
              ))}
            </div>
          )}

          {!loading && saves.length === 0 && (
            <EmptySavedEpisodes
              onSaved={() => {
                // Refresh saved list after a one-click save from the empty state.
                if (!user) return;
                supabase
                  .from("saved_episodes")
                  .select("id, episode_id, created_at, episode:episodes(id, title, slug, description, audio_url, published_at, duration_seconds, spotify_episode_url, apple_episode_url, podcast:podcasts(name, artwork_url, spotify_url))")
                  .eq("user_id", user.id)
                  .order("created_at", { ascending: false })
                  .then(({ data }) => {
                    if (data) setSaves((data as unknown as SavedEpisode[]) || []);
                  });
              }}
            />
          )}

          {!loading && saves.length > 0 && (
            <div className="space-y-3">
              <div className="mb-1">
                <div className="max-w-2xl mx-auto">
                  <p className="font-mono text-xs text-muted-foreground mb-2">
                    🔍 Discover more episodes to add to your library
                  </p>
                </div>
                <SearchBar size="compact" />
              </div>
              {saves.map((save) => {
                const ep = save.episode;
                const podcast = ep?.podcast;
                return (
                  <TiltCard
                    key={save.id}
                    className="card-press-sm"
                  >
                    {/* Top row: artwork + info + play button */}
                    <div className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
                      {podcast?.artwork_url ? (
                        <img
                          src={podcast.artwork_url}
                          alt=""
                          className="w-14 h-14 sm:w-16 sm:h-16 retro-border object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-14 h-14 sm:w-16 sm:h-16 retro-border bg-muted flex items-center justify-center flex-shrink-0">
                          <span className="font-retro text-lg text-muted-foreground">
                            {podcast?.name?.charAt(0) || "?"}
                          </span>
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <h3 className="font-retro font-bold text-sm sm:text-base leading-tight mb-1 truncate">
                          <Link to={`/episode/${ep?.slug || save.episode_id}`} className="hover:text-primary transition-colors hover:underline">
                            {decodeEntities(ep?.title || "Unknown episode")}
                          </Link>
                        </h3>
                        <p className="font-mono text-[11px] sm:text-xs text-muted-foreground">
                          {podcast?.name || "Unknown podcast"}
                          {ep?.published_at && (
                            <> · {new Date(ep.published_at).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</>
                          )}
                          {ep?.duration_seconds && <> · {formatDuration(ep.duration_seconds)}</>}
                        </p>
                      </div>

                      {ep?.audio_url && (
                        <button
                          onClick={() => togglePlay(ep.id)}
                          className={`comic-play-btn relative overflow-hidden flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 border-[3px] border-[hsl(195,80%,28%)] rounded-md bg-[hsl(195,85%,55%)] text-white flex items-center justify-center transition-all duration-150 transform shadow-[3px_3px_0px_0px_hsl(var(--foreground))] hover:shadow-[1px_1px_0px_0px_hsl(var(--foreground))] hover:translate-x-[2px] hover:translate-y-[2px] hover:scale-110 active:scale-90 ${
                            playingId === ep.id ? "rotate-0" : "rotate-[-2deg] hover:rotate-0"
                          }`}
                          title={playingId === ep.id ? "Pause" : "Play"}
                        >
                          {playingId === ep.id ? <Pause className="w-5 h-5 relative z-10" strokeWidth={3} /> : <Play className="w-5 h-5 ml-0.5 relative z-10" strokeWidth={3} fill="currentColor" />}
                        </button>
                      )}
                    </div>

                    {/* Bottom action bar */}
                    <div className="flex items-center justify-around border-t-2 border-border px-2 py-1.5">
                      <NoteButton
                        episodeId={ep.id}
                        episodeTitle={ep.title}
                        audioUrl={ep.audio_url}
                        durationSeconds={ep.duration_seconds}
                        podcastName={podcast?.name}
                        artworkUrl={podcast?.artwork_url}
                      />
                      <Link
                        to={`/episode/${ep?.slug || save.episode_id}`}
                        className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                        title="View episode"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                      <ShareEpisode
                        episodeSlug={ep?.slug || save.episode_id}
                        episodeTitle={ep?.title || "Episode"}
                        podcastName={podcast?.name}
                      />
                      {ep?.audio_url && (
                        <OfflineEpisodeButton
                          episodeId={ep.id}
                          audioUrl={ep.audio_url}
                          title={ep.title}
                          podcastName={podcast?.name || "Unknown podcast"}
                          artworkUrl={podcast?.artwork_url || undefined}
                          durationSeconds={ep.duration_seconds || undefined}
                        />
                      )}
                      <button
                        onClick={() => handleRemove(save.id)}
                        className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Inline audio player */}
                    {playingId === ep.id && ep.audio_url && (
                      <div className="border-t-2 border-border">
                        <AudioPlayer
                          src={ep.audio_url}
                          title={ep.title}
                          podcastName={podcast?.name}
                          artworkUrl={podcast?.artwork_url || undefined}
                          episodeId={ep.id}
                          autoPlay
                        />
                      </div>
                    )}
                  </TiltCard>
                );
              })}
            </div>
          )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
